import {
  fetchAllGeofenceEvents,
  fetchAllGeofenceVisits,
  fetchAllLocationPings,
  getGeofenceFunnel,
  getGeofenceJourneys,
  getGeofenceSummary,
  getGeofenceTimeBucket,
  getGeofenceVisits,
} from '@/services/geofence';

import type {
  TGeofenceStationId,
  TGetGeofenceEventsQuery,
  TGetGeofenceFunnelQuery,
  TGetGeofenceJourneysQuery,
  TGetGeofenceSummaryQuery,
  TGetGeofenceTimeBucketQuery,
  TGetGeofenceVisitsQuery,
} from '@/types/geofence';

import { stableParams, unwrapData } from './utils';

/** Params of a call that reads every page of a range (the fan-out sets `page` and `size`). */
export type TGeofenceAllParams<T> = Omit<T, 'page' | 'size'>;

/** Params of the location pings read: sites and date range. */
export type TGeofenceLocationPingsParams = {
  stationId: TGeofenceStationId;
  from: string;
  to: string;
};

/**
 * Keys of the geofence area (visit analytics of the sites). Every geofence query starts with
 * `geofenceKeys.all`, so a save can mark the whole area old.
 * - `summary`, `funnel`, `timeBucket`, `journeys`: the cards of the Visits Overview and the
 *   Dashboard stat cards. Each key holds every param (sites, dates, minimum dwell time).
 * - `visits`, `visitsAll`, `events`, `locationPings`: the Visits Records page, the Zones per Visit
 *   card, the Visit Density card, the Visit Records zones column and the Heatmap. The "All" and
 *   events and pings keys hold the result of a whole fan-out (every page of the range).
 * Invalidated by: W1 `updateStation` and W2 `uploadStationLogo` (whole `geofenceKeys.all`: the
 * zones and amenity locations of a site feed the Heatmap and Zones, and a rename changes site
 * names in the visit data).
 */
export const geofenceKeys = {
  all: ['geofence'] as const,
  summary: (params: TGetGeofenceSummaryQuery) =>
    [...geofenceKeys.all, 'summary', stableParams(params)] as const,
  funnel: (params: TGetGeofenceFunnelQuery) =>
    [...geofenceKeys.all, 'funnel', stableParams(params)] as const,
  timeBucket: (params: TGetGeofenceTimeBucketQuery) =>
    [...geofenceKeys.all, 'timeBucket', stableParams(params)] as const,
  journeys: (params: TGetGeofenceJourneysQuery) =>
    [...geofenceKeys.all, 'journeys', stableParams(params)] as const,
  visits: (params: TGetGeofenceVisitsQuery) =>
    [...geofenceKeys.all, 'visits', stableParams(params)] as const,
  visitsAll: (params: TGeofenceAllParams<TGetGeofenceVisitsQuery>) =>
    [...geofenceKeys.all, 'visitsAll', stableParams(params)] as const,
  /** Root of every events query: lets a caller read the saved ranges (see `useVisitDensity`). */
  eventsRoot: () => [...geofenceKeys.all, 'events'] as const,
  events: (params: TGeofenceAllParams<TGetGeofenceEventsQuery>) =>
    [...geofenceKeys.eventsRoot(), stableParams(params)] as const,
  locationPings: (params: TGeofenceLocationPingsParams) =>
    [...geofenceKeys.all, 'locationPings', stableParams(params)] as const,
};

/**
 * Query for the visit summary of the picked sites and dates (visits, customers, average dwell
 * time). Used by the Visits Overview stat cards, the Visitors card and the Dashboard stat cards.
 * What: resolves to the summary (`response.data.data`).
 * Key: `['geofence', 'summary', params]`; the station ids are sorted so one set of sites is one key.
 * Callers add `enabled` until the `minDwellSeconds` lookup is in, so the call never goes out twice
 * (first with the default 60, then with the real value). staleTime: app default.
 * Invalidated by: W1, W2.
 *
 * @param params Sites, date range and minimum dwell time.
 */
export const geofenceSummaryQuery = (params: TGetGeofenceSummaryQuery) => {
  const stable = stableParams(params);

  return {
    queryKey: geofenceKeys.summary(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getGeofenceSummary(stable, signal).then((response) => unwrapData(response.data.data)),
  };
};

/**
 * Query for the zone funnel (visits per amenity zone). Used by the Time in Zone and Zone
 * Analytics cards.
 * What: resolves to the funnel (`response.data.data`, includes `amenityBreakdown`).
 * Key: `['geofence', 'funnel', params]`. Callers add `enabled` until the `minDwellSeconds` lookup
 * is in. staleTime: app default. Invalidated by: W1, W2.
 *
 * @param params Sites, date range and minimum dwell time.
 */
export const geofenceFunnelQuery = (params: TGetGeofenceFunnelQuery) => {
  const stable = stableParams(params);

  return {
    queryKey: geofenceKeys.funnel(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getGeofenceFunnel(stable, signal).then((response) => unwrapData(response.data.data)),
  };
};

/**
 * Query for the time-on-site buckets (how long visits last). Used by the Time on Site card.
 * What: resolves to `{ buckets }` (`response.data.data`).
 * Key: `['geofence', 'timeBucket', params]`. Callers add `enabled` until the `minDwellSeconds`
 * lookup is in. staleTime: app default. Invalidated by: W1, W2.
 *
 * @param params Sites, date range and minimum dwell time.
 */
export const geofenceTimeBucketQuery = (params: TGetGeofenceTimeBucketQuery) => {
  const stable = stableParams(params);

  return {
    queryKey: geofenceKeys.timeBucket(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getGeofenceTimeBucket(stable, signal).then((response) => unwrapData(response.data.data)),
  };
};

/**
 * Query for the top customer journeys (most common paths between amenity zones). Used by the Top
 * Customer Journeys card. Callers add `enabled` until the `minDwellSeconds` lookup is in.
 * What: resolves to `{ totalVisits, topPaths }` (`response.data.data`).
 * Key: `['geofence', 'journeys', params]`. staleTime: app default. Invalidated by: W1, W2.
 *
 * @param params Sites, date range and path settings (`topPaths`, `dedupePathAmenities`) and minimum dwell time.
 */
export const geofenceJourneysQuery = (params: TGetGeofenceJourneysQuery) => {
  const stable = stableParams(params);

  return {
    queryKey: geofenceKeys.journeys(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getGeofenceJourneys(stable, signal).then((response) => unwrapData(response.data.data)),
  };
};

/**
 * Query for ONE page of the Visits Records list.
 * What: resolves to the raw page (`response.data.data`: `content` or `result`, `totalElements` or
 * `totalHits`). The page reads the rows and the total from it.
 * Why page, search and sites are in the key: a page, search or site change reads its own entry, so
 * rows of another page or search never show and a slow old answer cannot replace the new rows.
 * Key: `['geofence', 'visits', { stationId, from, to, userSearch, minDwellSeconds, page, size }]`,
 * site ids sorted, undefined dropped. Callers add `enabled` until the `minDwellSeconds` lookup is in.
 * staleTime: app default. Invalidated by: W1, W2.
 *
 * @param params Sites, dates, search text, minimum dwell time, page and page size.
 */
export const geofenceVisitsQuery = (params: TGetGeofenceVisitsQuery) => {
  const stable = stableParams(params);

  return {
    queryKey: geofenceKeys.visits(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getGeofenceVisits(stable, signal).then((response) => unwrapData(response.data.data)),
  };
};

/**
 * Query for EVERY visit of a range (all pages, one result). Used by the Zones per Visit card.
 * What: resolves to the list of visits. The service reads page after page and passes the `signal`
 * to every page call, so leaving the page stops the whole fan-out.
 * Key: `['geofence', 'visitsAll', { stationId, from, to, status, userSearch, minDwellSeconds }]`.
 * Callers add `enabled` until the `minDwellSeconds` lookup is in. staleTime: app default.
 * Invalidated by: W1, W2.
 *
 * @param params Sites, dates and minimum dwell time (no page: the fan-out sets it).
 */
export const geofenceVisitsAllQuery = (params: TGeofenceAllParams<TGetGeofenceVisitsQuery>) => {
  const stable = stableParams(params);

  return {
    queryKey: geofenceKeys.visitsAll(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchAllGeofenceVisits(stable, signal),
  };
};

/**
 * Query for EVERY geofence event of a range (all pages, one result). Used by the Visit Density
 * card (station ENTER events) and the Visits Records zones column (amenity ENTER events).
 * What: resolves to the list of events. The `signal` reaches every page call.
 * Key: `['geofence', 'events', { stationId, from, to, entityType, eventType, minDwellSeconds }]`:
 * the two cards use different `entityType`, so they never share an entry. Callers add `enabled`
 * until the `minDwellSeconds` lookup is in. staleTime: app default. Invalidated by: W1, W2.
 *
 * @param params Sites, dates, entity and event type, minimum dwell time (no page).
 */
export const geofenceEventsQuery = (params: TGeofenceAllParams<TGetGeofenceEventsQuery>) => {
  const stable = stableParams(params);

  return {
    queryKey: geofenceKeys.events(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchAllGeofenceEvents(stable, signal),
  };
};

/**
 * Query for EVERY location ping of a range (all pages, one result). Used by the Heatmap.
 * What: resolves to `{ pings, totalPings, uniqueUsers, capped }`. The `signal` reaches every page
 * call, so leaving the page or changing the dates cancels the running fan-out.
 * Key: `['geofence', 'locationPings', { stationId, from, to }]`, site ids sorted. No `enabled`
 * needed: the pings call does not use the minimum dwell time. staleTime: app default.
 * Invalidated by: W1, W2.
 *
 * @param params Sites and date range.
 */
export const geofenceLocationPingsQuery = (params: TGeofenceLocationPingsParams) => {
  const stable = stableParams(params);

  return {
    queryKey: geofenceKeys.locationPings(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchAllLocationPings(stable.stationId, { from: stable.from, to: stable.to }, signal),
  };
};
